import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import userManagementService from '../../../services/userManagementService';
import departmentsService from '../../../services/departmentsService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import NotificationToast from '../../../components/ui/NotificationToast';

const UserManagement = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({ search: '', role: 'all', department: 'all' });

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    department: '',
    role: 'employee',
    password: ''
  });

  const [permissions, setPermissions] = useState({});

  const roles = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'hr_admin', label: 'HR Admin' },
    { value: 'finance_admin', label: 'Finance Admin' },
    { value: 'employee', label: 'Employee' },
    { value: 'manager', label: 'Manager' },
    { value: 'telecaller', label: 'Telecaller' },
    { value: 'staff', label: 'Staff' },
    { value: 'client', label: 'Client' }
  ];

  const permissionTypes = ['all', 'create', 'read', 'update', 'delete', 'commercial'];

  useEffect(() => {
    loadUsers();
    loadDepartments();
    loadModules();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await userManagementService.getUsers(filters);
      if (result.success) {
        setUsers(result.data || []);
      } else {
        setError(result.error || 'Failed to load users');
      }
    } catch (err) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const result = await departmentsService.getDepartments();
      if (result.success) {
        setDepartments(result.data || []);
      }
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const loadModules = async () => {
    try {
      const result = await userManagementService.getModules();
      if (result.success) {
        setModules(result.data || {});
      }
    } catch (err) {
      console.error('Error loading modules:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAdd = () => {
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      department: '',
      role: 'employee',
      password: ''
    });
    setSelectedUser(null);
    setShowAddModal(true);
  };

  const handleEdit = (user) => {
    setFormData({
      email: user.email,
      full_name: user.full_name,
      phone: user.phone || '',
      department: user.department || '',
      role: user.role,
      password: ''
    });
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handlePermissions = (user) => {
    setSelectedUser(user);
    // Initialize permissions from user data or empty object
    const userPermissions = user.permissions || {};
    setPermissions(userPermissions);
    setShowPermissionsModal(true);
  };

  const handlePermissionChange = (module, permissionType, value) => {
    setPermissions(prev => {
      const newPerms = { ...prev };
      if (!newPerms[module]) {
        newPerms[module] = {};
      }

      // If "all" is checked, set all others to true
      if (permissionType === 'all' && value) {
        permissionTypes.forEach(type => {
          newPerms[module][type] = true;
        });
      }
      // If "all" is unchecked, set all others to false
      else if (permissionType === 'all' && !value) {
        permissionTypes.forEach(type => {
          newPerms[module][type] = false;
        });
      }
      // If any other permission is changed, update "all" based on all others
      else {
        newPerms[module][permissionType] = value;
        const allOthersTrue = permissionTypes
          .filter(type => type !== 'all')
          .every(type => newPerms[module][type] === true);
        newPerms[module]['all'] = allOthersTrue;
      }

      return newPerms;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const result = await userManagementService.deleteUser(id);
      if (result.success) {
        showNotification('User deleted successfully');
        await loadUsers();
      } else {
        showNotification(result.error || 'Failed to delete user', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to delete user', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const isEdit = !!selectedUser;
      const result = isEdit
        ? await userManagementService.updateUser(selectedUser.id, formData)
        : await userManagementService.createUser(formData);

      if (result.success) {
        showNotification(`User ${isEdit ? 'updated' : 'created'} successfully`);
        setShowAddModal(false);
        setShowEditModal(false);
        setSelectedUser(null);
        setFormData({
          email: '',
          full_name: '',
          phone: '',
          department: '',
          role: 'employee',
          password: ''
        });
        await loadUsers();
      } else {
        showNotification(result.error || `Failed to ${isEdit ? 'update' : 'create'} user`, 'error');
      }
    } catch (err) {
      showNotification(err.message || `Failed to ${selectedUser ? 'update' : 'create'} user`, 'error');
    }
  };

  const handleSavePermissions = async () => {
    try {
      const result = await userManagementService.updateUser(selectedUser.id, { permissions });
      if (result.success) {
        showNotification('Permissions updated successfully');
        setShowPermissionsModal(false);
        setSelectedUser(null);
        await loadUsers();
      } else {
        showNotification(result.error || 'Failed to update permissions', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to update permissions', 'error');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !filters.search ||
      user.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.phone?.includes(filters.search);

    const matchesRole = filters.role === 'all' || user.role === filters.role;
    const matchesDepartment = filters.department === 'all' || user.department === filters.department;

    return matchesSearch && matchesRole && matchesDepartment;
  });

  const uniqueDepartments = [...new Set(users.map(u => u.department).filter(Boolean))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground text-sm">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">User Management</h2>
          <p className="text-muted-foreground text-sm mt-1">Manage all system users, departments, and permissions</p>
        </div>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Icon name="UserPlus" size={18} />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Search by name, email, or phone..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full"
          />
          <Select
            value={filters.role}
            onValueChange={(value) => setFilters({ ...filters, role: value })}
            className="w-full"
            options={[
              { value: 'all', label: 'All Roles' },
              ...roles
            ]}
          />
          <Select
            value={filters.department}
            onValueChange={(value) => setFilters({ ...filters, department: value })}
            className="w-full"
            options={[
              { value: 'all', label: 'All Departments' },
              ...uniqueDepartments.map(dept => ({ value: dept, label: dept }))
            ]}
          />
        </div>
      </div>

      {/* Users Table */}
      {error ? (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4 text-destructive">
          {error}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{user.full_name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.email}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {roles.find(r => r.value === user.role)?.label || user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.department || '-'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{user.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            title="Edit"
                          >
                            <Icon name="Edit" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePermissions(user)}
                            title="Permissions"
                          >
                            <Icon name="Shield" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            title="Delete"
                            className="text-destructive hover:text-destructive"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">Add New User</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <Icon name="X" size={18} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Input
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <Select
                label="Role"
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                required
                options={roles}
              />
              <Select
                label="Department"
                value={formData.department}
                onValueChange={(value) => setFormData({ ...formData, department: value })}
                options={[
                  { value: '', label: 'Select Department' },
                  ...departments.map(dept => ({ value: dept.name, label: dept.name }))
                ]}
              />
              <Input
                label="Password (default: User@123)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Leave empty for default password"
              />
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">Create User</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-foreground">Edit User</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                <Icon name="X" size={18} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Full Name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Input
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <Select
                label="Role"
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                required
                options={roles}
              />
              <Select
                label="Department"
                value={formData.department}
                onValueChange={(value) => setFormData({ ...formData, department: value })}
                options={[
                  { value: '', label: 'Select Department' },
                  ...departments.map(dept => ({ value: dept.name, label: dept.name }))
                ]}
              />
              <Input
                label="New Password (leave empty to keep current)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter new password to change"
              />
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">Update User</Button>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Module Permissions</h3>
                <p className="text-sm text-muted-foreground mt-1">User: {selectedUser.full_name} ({selectedUser.email})</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPermissionsModal(false)}>
                <Icon name="X" size={18} />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-foreground">Module</th>
                      {permissionTypes.map(type => (
                        <th key={type} className="px-4 py-3 text-center font-medium text-foreground capitalize">
                          {type === 'all' ? 'All' : type}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {Object.entries(modules).map(([moduleKey, moduleName]) => (
                      <tr key={moduleKey} className="hover:bg-muted/50">
                        <td className="px-4 py-3 font-medium text-foreground">{moduleName}</td>
                        {permissionTypes.map(type => (
                          <td key={type} className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={permissions[moduleKey]?.[type] === true}
                              onChange={(e) => handlePermissionChange(moduleKey, type, e.target.checked)}
                              className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex gap-2 pt-4 border-t border-border">
                <Button onClick={handleSavePermissions} className="flex-1">Save Permissions</Button>
                <Button type="button" variant="outline" onClick={() => setShowPermissionsModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

